import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LivingNeuralNetwork from './LivingNeuralNetwork';
import ModernLoader from './ModernLoader';

const NeuralGateway = ({ onEnterPortfolio }) => {
  const [showGateway, setShowGateway] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSkip = useCallback(() => {
    setShowLoader(true);
    setTimeout(() => {
      setShowGateway(false);
      onEnterPortfolio();
    }, 2000); // Longer delay for smooth transition
  }, [onEnterPortfolio]);

  useEffect(() => {
    // Auto-continue after 2 minutes if user doesn't interact
    const autoSkipTimer = setTimeout(() => {
      handleSkip();
    }, 120000);

    return () => clearTimeout(autoSkipTimer);
  }, [handleSkip]);

  if (!showGateway) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.8 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
          overflow: 'hidden'
        }}
      >
        {/* Skip Button now integrated into Pro Tips modal below */}

        {/* Animated background particles */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.1
        }}>
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                width: '2px',
                height: '2px',
                background: '#60a5fa',
                borderRadius: '50%',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>

        {/* Main Gateway Content */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          style={{
            position: 'absolute',
            top: isMobile ? '5px' : '10px',
            left: isMobile ? '50%' : '19%', // Center on mobile, offset on desktop
            transform: 'translateX(-50%)',
            textAlign: 'center',
            zIndex: 10,
            padding: isMobile ? '0 10px' : '0 20px', // Less padding on mobile
            width: isMobile ? '90%' : '100%', // Less width on mobile
            maxWidth: isMobile ? '400px' : '800px', // Smaller max width on mobile
            pointerEvents: 'none', // Don't block clicks to neural network
            // Ensure it doesn't extend into neural network area
            height: isMobile ? '60px' : 'auto', // Fixed height on mobile
            overflow: 'hidden' // Prevent content from overflowing
          }}
        >
          <motion.h1
            style={{
              fontSize: isMobile ? 'clamp(1.5rem, 5vw, 2.2rem)' : 'clamp(2.5rem, 5vw, 4rem)', // Smaller on mobile
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #60a5fa, #a855f7, #ff6b35)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: isMobile ? '12px' : '20px', // Less margin on mobile
              textAlign: 'center',
              pointerEvents: 'auto', // Allow text selection if needed
              userSelect: 'none' // But prevent selection
            }}
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'linear'
            }}
          >
            Joseph's Project Universe
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            style={{
              fontSize: isMobile ? 'clamp(0.8rem, 3vw, 1rem)' : 'clamp(0.7rem, 1vw, 1.3rem)', // Better mobile sizing
              color: '#d1d5db',
              maxWidth: isMobile ? '350px' : '600px', // Smaller on mobile
              margin: '0 auto',
              marginBottom: isMobile ? '20px' : '40px', // Less bottom margin on mobile
              lineHeight: 1.4, // Tighter line height
              padding: '0 5px', // Small padding
              pointerEvents: 'auto', // Allow text selection if needed
              userSelect: 'none' // But prevent selection
            }}
          >
            {isMobile
              ? "Explore my 50+ projects • Click nodes → Discover connections"
              : "Explore my 50+ projects through an intelligent neural network • Click → Expand → Discover infinite connections"
            }
          </motion.p>
        </motion.div>

        {/* Living Neural Network */}
        <motion.div
          className="neural-network"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2, duration: 1 }}
          style={{
            position: 'absolute',
            top: isMobile ? '70px' : '120px', // Adjusted for more compact title
            left: isMobile ? '2px' : '10px', // Minimal left margin on mobile
            right: isMobile ? '2px' : '10px', // Minimal right margin on mobile
            bottom: isMobile ? '70px' : '100px', // Adjusted for progress indicator
            zIndex: 15, // Higher than title (10) and progress (10) to prevent overlap
            padding: isMobile ? '0 2px' : '0 10px' // Minimal padding on mobile
          }}
        >
          <LivingNeuralNetwork
            isGateway={true}
            onSkip={null} // Disable internal skip button since we handle it at gateway level
          />
        </motion.div>

        {/* Progress Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3 }}
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: '#9ca3af',
            fontSize: '0.9rem',
            zIndex: 10
          }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{
              width: '16px',
              height: '16px',
              border: '2px solid #60a5fa',
              borderTop: '2px solid transparent',
              borderRadius: '50%'
            }}
          />
          Network is learning from your interactions...
        </motion.div>

        {/* Floating Action Hint - show on both mobile and desktop */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: 0,
            x: isDragging ? 0 : [0, -5, 5, -3, 3, 0], // Bouncing effect when not dragging
          }}
          transition={{
            delay: 2.5,
            duration: 1,
            x: {
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3,
              ease: "easeInOut"
            }
          }}
          drag
          dragConstraints={{
            left: -window.innerWidth + 50,
            right: window.innerWidth - 50,
            top: -window.innerHeight + 50,
            bottom: window.innerHeight - 50
          }}
          dragElastic={0.1}
          dragMomentum={false}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => setIsDragging(false)}
          onClick={(e) => {
            if (!isDragging) {
              console.log('Pro Tips clicked!');
            }
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          whileDrag={{ scale: 1.1, rotate: 5 }}
          style={{
            position: 'absolute',
            top: isMobile ? '40%' : '50%', // Higher on mobile to avoid overlap
            right: isMobile ? '10px' : '50px', // Closer to edge on mobile
            transform: 'translateY(-50%)',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(15px)',
            border: '2px solid rgba(96, 165, 250, 0.3)', // More visible border to show it's interactive
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(96, 165, 250, 0.1)',
            padding: isMobile ? '16px' : '22px', // Slightly more padding for button
            color: 'white',
            maxWidth: isMobile ? '220px' : '270px', // Slightly larger to fit button
            zIndex: 25, // Much higher than neural network to ensure clickability
            pointerEvents: 'auto', // Ensure it's interactive
            fontSize: isMobile ? '0.8rem' : '0.9rem', // Smaller text on mobile
            cursor: isDragging ? 'grabbing' : 'grab', // Show drag state
            transition: 'all 0.2s ease',
            // Add visual indicator that it's draggable
            borderTop: '4px solid rgba(96, 165, 250, 0.6)'
          }}
        >
          {/* Drag Handle Indicator */}
          <div
            style={{
              position: 'absolute',
              top: '-2px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '40px',
              height: '4px',
              background: 'rgba(96, 165, 250, 0.8)',
              borderRadius: '2px',
              cursor: 'grab',
              opacity: isDragging ? 0.8 : 0.4,
              transition: 'opacity 0.2s ease'
            }}
          />

          <h4
            style={{
              margin: '0 0 8px 0',
              color: '#60a5fa',
              fontSize: '1.1rem',
              cursor: 'pointer',
              userSelect: 'none'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDragging) {
                console.log('Pro Tips header clicked!');
              }
            }}
          >
            💡 Pro Tips
          </h4>

          <div
            style={{
              fontSize: '0.7rem',
              color: 'rgba(96, 165, 250, 0.7)',
              marginBottom: '8px',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Drag me to move out of the way
          </div>
          <ul style={{
            margin: 0,
            padding: '0 0 0 16px',
            fontSize: '0.9rem',
            lineHeight: 1.6,
            color: '#d1d5db'
          }}>
            <li>Click nodes to see skills & projects</li>
            <li>Network learns from your exploration</li>
            <li>Each visitor makes it smarter</li>
            <li>Watch for thought particles</li>
          </ul>

          {/* Skip Button integrated into the modal */}
          <motion.button
            onClick={(e) => {
              e.stopPropagation(); // Prevent modal click
              handleSkip();
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              marginTop: '16px',
              padding: isMobile ? '10px 20px' : '12px 24px',
              background: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(96, 165, 250, 0.4)',
              transition: 'all 0.2s ease'
            }}
          >
            {isMobile ? '🚀 Skip to Portfolio' : '🚀 Skip to Portfolio →'}
          </motion.button>
          </motion.div>

        {/* Modern Loader Overlay */}
        <AnimatePresence>
          {showLoader && (
            <ModernLoader message="Entering Portfolio..." />
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default NeuralGateway;
